import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { dismissBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import {
  isLightweightDataSession,
  isLightweightGenericDataset,
} from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightAncestor,
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import type {
  LightweightElement,
  LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { BackendFileExportTaskBase } from "@/utils/background-tasks";
import {
  downloadFile,
  downloadFileAsBlob,
  downloadLargeFile,
} from "@/utils/download";
import {
  getFileExtension,
  NoTranslate,
  removeExtension,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { GUID } from "@faro-lotv/ielement-types";
import {
  requestSignedUrlForIElement,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
  ProgressApiSupportedTaskTypes,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import {
  getTaskStatusLabel,
  getTaskTypeDescription,
} from "../task-descriptions";

type BackendFileExportCardProps = {
  /** The export task */
  task: BackendFileExportTaskBase;

  /**
   * If true, the export is for an image
   *
   * @default false
   */
  isImageExport?: boolean;

  /**
   * If true, the export is for a pdf
   *
   * @default false
   */
  isPDFExport?: boolean;

  /**
   * If true, the export files are expected to get very large (>1GB)
   *
   * @default false
   */
  isLargeExport?: boolean;
};

/**
 * A memoized selector to determine the reference element for a given element ID
 *
 * @param state The state of the app store
 * @param id The data of the element to find the reference for
 * @returns the memoized selector
 */
const selectRefElement = curryAppSelector(
  createSelector(
    [selectLightweightElementsSlice, (state: RootState, id: GUID) => id],
    computeRefElement,
  ),
);

/**
 * @param lightweightElements The lightweight elements slice of the store
 * @param id The ID of the element to find the reference for
 * @returns the reference element for a given element ID
 */
function computeRefElement(
  lightweightElements: LightweightElementsState,
  id: GUID,
): LightweightElement | undefined {
  return selectLightweightAncestor(
    selectLightweightElement(id)({ lightweightElements }),
    (e) => isLightweightDataSession(e) || isLightweightGenericDataset(e),
  )({ lightweightElements });
}

/** @returns The card for the backend file export tasks */
export function BackendFileExportCard({
  task,
  isImageExport = false,
  isPDFExport = false,
  isLargeExport = false,
}: BackendFileExportCardProps): JSX.Element {
  const dispatch = useAppDispatch();
  const refElement = useAppSelector(selectRefElement(task.iElementId ?? ""));

  const rootId = useAppSelector(selectRootId);

  const [isSigningUrl, setIsSigningUrl] = useState(false);

  const { handleErrorWithToast } = useErrorHandlers();
  const { projectApiClient } = useApiClientContext();

  const downloadExportedFile = useCallback(async () => {
    setIsSigningUrl(true);
    try {
      const elementId = task.iElementId ?? rootId;

      assert(task.metadata.downloadUrl, "No download URL available.");
      assert(elementId, "No element ID available.");

      const signedDownloadUrl = await requestSignedUrlForIElement(
        projectApiClient,
        task.metadata.downloadUrl,
        elementId,
      );

      if (signedDownloadUrl instanceof Error) throw signedDownloadUrl;

      if (isImageExport) {
        downloadFileAsBlob(
          signedDownloadUrl,
          task.metadata.fileName ??
            `image-export.${getFileExtension(task.metadata.downloadUrl)}`,
        );
      } else if (isLargeExport) {
        await downloadLargeFile(signedDownloadUrl, task.metadata.fileName);
      } else {
        downloadFile(signedDownloadUrl, undefined, isPDFExport);
      }
    } catch (error) {
      handleErrorWithToast({
        title: "Could not start download",
        error,
      });
    } finally {
      setIsSigningUrl(false);
    }
  }, [
    handleErrorWithToast,
    isImageExport,
    isLargeExport,
    isPDFExport,
    projectApiClient,
    rootId,
    task.iElementId,
    task.metadata.downloadUrl,
    task.metadata.fileName,
  ]);

  const dismissErrorTask = useCallback((): void => {
    dispatch(dismissBackgroundTask(task.id));
  }, [dispatch, task.id]);

  const fileName = useMemo<string | JSX.Element>(() => {
    if (task.metadata.fileName) {
      return (
        <NoTranslate>{removeExtension(task.metadata.fileName)}</NoTranslate>
      );
    } else if (refElement) {
      return <NoTranslate>{refElement.name}</NoTranslate>;
    }

    return "Export";
  }, [refElement, task.metadata.fileName]);

  const subText = useMemo(() => {
    const description = getTaskTypeDescription(task);
    if (task.metadata.fileName) {
      return (
        <>
          {description}{" "}
          <NoTranslate component="span">
            (.{getFileExtension(task.metadata.fileName)})
          </NoTranslate>
        </>
      );
    }
    return description;
  }, [task]);

  const errorHint =
    task.type === ProgressApiSupportedTaskTypes.structuredExport &&
    task.errorCode === "PARTIAL_SUCCESS"
      ? "Please verify the scans are valid and try again."
      : undefined;

  const actions = useMemo<CardAction | undefined>(() => {
    if (task.state === BackgroundTaskState.failed) {
      return { name: "Clear", callback: dismissErrorTask };
    }
    if (task.metadata.downloadUrl) {
      return {
        name: "Download",
        callback: downloadExportedFile,
        isLoading: isSigningUrl,
      };
    }
  }, [
    task.state,
    task.metadata.downloadUrl,
    dismissErrorTask,
    downloadExportedFile,
    isSigningUrl,
  ]);

  return (
    <GenericCardLayout
      name={fileName}
      subText={subText}
      startTime={task.createdAt}
      action={actions}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress label={getTaskStatusLabel(task)} task={task} />
      )}
      {task.state === BackgroundTaskState.failed && (
        <CardError
          task={task}
          hint={errorHint}
          showDetails={
            task.type === ProgressApiSupportedTaskTypes.structuredExport &&
            task.errorCode === "PARTIAL_SUCCESS"
          }
        />
      )}
    </GenericCardLayout>
  );
}
